import { useState, useEffect, useRef } from "react";

const ICON_BASE = "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons";

const iconMap = {
  "Java": `${ICON_BASE}/java/java-original.svg`,
  "Python": `${ICON_BASE}/python/python-original.svg`,
  "C/C++": `${ICON_BASE}/cplusplus/cplusplus-original.svg`,
  "JavaScript": `${ICON_BASE}/javascript/javascript-original.svg`,
  "TypeScript": `${ICON_BASE}/typescript/typescript-original.svg`,
  "PHP": `${ICON_BASE}/php/php-original.svg`,
  "Kotlin": `${ICON_BASE}/kotlin/kotlin-original.svg`,
  "React.js": `${ICON_BASE}/react/react-original.svg`,
  "Next.js": `${ICON_BASE}/nextjs/nextjs-original.svg`,
  "Angular": `${ICON_BASE}/angular/angular-original.svg`,
  "Node.js": `${ICON_BASE}/nodejs/nodejs-original.svg`,
  "Express.js": `${ICON_BASE}/express/express-original.svg`,
  "React Native": `${ICON_BASE}/react/react-original.svg`,
  ".NET": `${ICON_BASE}/dot-net/dot-net-original.svg`,
  "HTML/CSS": `${ICON_BASE}/html5/html5-original.svg`,
  "MongoDB": `${ICON_BASE}/mongodb/mongodb-original.svg`,
  "MySQL": `${ICON_BASE}/mysql/mysql-original.svg`,
  "AWS": `${ICON_BASE}/amazonwebservices/amazonwebservices-plain-wordmark.svg`,
  "Azure": `${ICON_BASE}/azure/azure-original.svg`,
  "Docker": `${ICON_BASE}/docker/docker-original.svg`,
  "Git": `${ICON_BASE}/git/git-original.svg`,
};

const skillsData = {
  all: [
    { name: "Java", icon: "☕" },
    { name: "Python", icon: "🐍" },
    { name: "C/C++", icon: "⚡" },
    { name: "JavaScript", icon: "✨" },
    { name: "TypeScript", icon: "📘" },
    { name: "PHP", icon: "🐘" },
    { name: "Kotlin", icon: "🟣" },
    { name: "React.js", icon: "⚛️" },
    { name: "Next.js", icon: "▲" },
    { name: "Angular", icon: "🅰️" },
    { name: "Node.js", icon: "🟢" },
    { name: "Express.js", icon: "🚀" },
    { name: "React Native", icon: "📱" },
    { name: ".NET", icon: "🔷" },
    { name: "HTML/CSS", icon: "🎨" },
    { name: "MongoDB", icon: "🍃" },
    { name: "MySQL", icon: "🐬" },
    { name: "AWS", icon: "☁️" },
    { name: "Azure", icon: "💠" },
    { name: "Docker", icon: "🐳" },
    { name: "Git", icon: "📦" },
  ],
  frontend: [
    { name: "React.js", icon: "⚛️" },
    { name: "Next.js", icon: "▲" },
    { name: "Angular", icon: "🅰️" },
    { name: "TypeScript", icon: "📘" },
    { name: "JavaScript", icon: "✨" },
    { name: "HTML/CSS", icon: "🎨" },
    { name: "React Native", icon: "📱" },
  ],
  backend: [
    { name: "Node.js", icon: "🟢" },
    { name: "Express.js", icon: "🚀" },
    { name: "Java", icon: "☕" },
    { name: "Python", icon: "🐍" },
    { name: "PHP", icon: "🐘" },
    { name: ".NET", icon: "🔷" },
    { name: "C/C++", icon: "⚡" },
    { name: "Kotlin", icon: "🟣" },
  ],
  tools: [
    { name: "MongoDB", icon: "🍃" },
    { name: "MySQL", icon: "🐬" },
    { name: "AWS", icon: "☁️" },
    { name: "Azure", icon: "💠" },
    { name: "Docker", icon: "🐳" },
    { name: "Git", icon: "📦" },
  ],
};

export const Skills = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [isRevealed, setIsRevealed] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const categories = [
    { key: "all", label: "All" },
    { key: "frontend", label: "Frontend" },
    { key: "backend", label: "Backend" },
    { key: "tools", label: "Tools & Cloud" },
  ];

  return (
    <section className="skill" id="skills" ref={sectionRef}>
      <div className="container">
        <div className={`skill-header reveal ${isRevealed ? "revealed" : ""}`}>
          <div className="section-tag">
            <span className="tag-dot"></span>
            Skills
          </div>
          <h2 className="section-title">
            My <span className="gradient-text">Tech Stack</span>
          </h2>
          <p className="section-subtitle" style={{ margin: "0 auto" }}>
            Technologies I work with to bring ideas to life — from frontend
            frameworks to cloud infrastructure.
          </p>
        </div>

        <div className={`skill-categories reveal ${isRevealed ? "revealed" : ""} reveal-delay-1`}>
          {categories.map((cat) => (
            <button
              key={cat.key}
              className={`skill-cat-btn ${activeCategory === cat.key ? "active" : ""}`}
              onClick={() => setActiveCategory(cat.key)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className={`skill-grid reveal ${isRevealed ? "revealed" : ""} reveal-delay-2`}>
          {skillsData[activeCategory].map((skill, index) => (
            <div
              className="skill-card"
              key={`${activeCategory}-${index}`}
              style={{
                animationDelay: `${index * 0.05}s`,
                animation: isRevealed ? `slideInUp 0.5s ease-out ${index * 0.05}s backwards` : "none",
              }}
            >
              <span className="skill-icon">
                <img 
                  src={iconMap[skill.name]} 
                  alt={skill.name} 
                  width="28" 
                  height="28" 
                  style={
                    skill.name === "Express.js" || skill.name === "Next.js"
                      ? { filter: "invert(1) brightness(2)" }
                      : {}
                  }
                />
              </span>
              <span className="skill-name">{skill.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
